import { useLazyGetCompleteAiPendingTaskQuery } from 'api/collaborateApi';
import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '../store';
import { setAiTaskData } from '../store/siteInfoSlice';
import { getPageId, removeIsLoadingAIDesignReview, setIsLoadingAIDesignReview } from '../store/tasksSlice';
import useGetPageId from './use-get-pageid';

// Type definitions for better type safety
interface AiReviewTaskEvent {
  pageId: string;
  status: 'pending' | 'running' | 'progressed' | 'completed' | 'failed';
}

export function useRealtimeAiReviewTask() {
  const dispatch = useAppDispatch();
  const { pageId: hookPageId } = useGetPageId();
  const storePageId = useAppSelector(getPageId);
  const pageId = storePageId || hookPageId;
  const [getReviewedPageIssues] = useLazyGetCompleteAiPendingTaskQuery();


  // Centralized function to fetch and update AI task data
  const fetchAndUpdateAiTaskData = useCallback(async (): Promise<boolean> => {
    if (!pageId) return false;

    try {
      const reviewedIssues = await getReviewedPageIssues(pageId).unwrap();

      if (reviewedIssues?.status === 'success') {
        dispatch(setAiTaskData(reviewedIssues.result.data));
        return true;
      } else {
        console.warn('AI review task fetch returned non-success status:', reviewedIssues?.status);
        return false;
      }
    } catch (error) {
      console.error('Failed to fetch AI review task data:', error);
      toast.error('Failed to load AI review data');
      return false;
    }
  }, [dispatch, pageId, getReviewedPageIssues]);

  // Handle AI review task status changes
  const handleAiReviewTask = useCallback(
    async (event: AiReviewTaskEvent) => {
      const { pageId: eventPageId, status } = event;

      switch (status) {
        case 'pending':
        case 'running':
          dispatch(setIsLoadingAIDesignReview({ page_id: eventPageId, status }));
          break;

        case 'progressed':
          // For progressed status, update data but keep loading state
          await fetchAndUpdateAiTaskData();
          break;

        case 'completed':
          dispatch(removeIsLoadingAIDesignReview(eventPageId));
          await fetchAndUpdateAiTaskData();
          break;

        case 'failed':
          dispatch(removeIsLoadingAIDesignReview(eventPageId));
          toast.error('AI design review failed');
          break;

        default:
          console.warn('Unknown AI review task status:', status);
      }
    },
    [dispatch, fetchAndUpdateAiTaskData]
  );

  // Handle completion event (legacy support)
  const handleAiReviewTaskCompleted = useCallback(async () => {
    await fetchAndUpdateAiTaskData();
  }, [fetchAndUpdateAiTaskData]);

  // Handle failure event
  const handleAiReviewTaskFailed = useCallback((error: unknown) => {
    console.error('AI review task failed:', error);
    toast.error('AI design review failed');
  }, []);

  // Set up real-time listeners
  useEffect(() => {
    if (!pageId || !window.Echo) return;

    const channelName = `pages.${pageId}.ai-design-review`;

    try {
      const privateChannel = window.Echo.private(channelName);

      // Listen for AI review events
      privateChannel
        .listen('.ai-design-review.completed', handleAiReviewTaskCompleted);

      privateChannel
        .listen('.AiDesignReviewStatusChanged', handleAiReviewTask);

      privateChannel
        .listen('.ai-design-review.failed', handleAiReviewTaskFailed);

      // Cleanup function
      return () => {
        try {
          privateChannel
            .stopListening('.ai-design-review.completed');
          privateChannel
            .stopListening('.ai-design-review.failed');
          privateChannel
            .stopListening('.AiDesignReviewStatusChanged');

          window.Echo.leaveChannel(channelName);
        } catch (cleanupError) {
          console.warn('Error during Echo cleanup:', cleanupError);
        }
      };
    } catch (error) {
      console.error('Failed to set up Echo listeners:', error);
      toast.error('Failed to connect to real-time updates');
    }
  }, [pageId, handleAiReviewTaskCompleted, handleAiReviewTask, handleAiReviewTaskFailed]);

  // Return utility functions for external use if needed
  return {
    refreshAiTaskData: fetchAndUpdateAiTaskData,
    isConnected: Boolean(pageId && window.Echo),
  };
}